<template>
  <div>
    <PageHeader title="Quick Send" />
    <b-card no-body>
      <b-tabs card>
        <!-- Single Email Tab -->
        <b-tab title="Single Email" active>
          <b-form @submit.prevent="handleSend">
            <b-form-group label="From Email">
              <b-form-input v-model="form.from_email" placeholder="sender@school.com" required />
            </b-form-group>
            <b-form-group label="Select Contact">
              <v-select
                v-model="form.contact_id"
                :options="contacts"
                label="text"
                :reduce="c => c.value"
                placeholder="Search contacts..."
              />
            </b-form-group>
            <b-form-group label="Use Template (optional)">
              <b-form-select v-model="form.template_id" :options="templateOptions" @change="onTemplateChange(form, 'single')" />
            </b-form-group>
            <b-form-group label="Subject" v-if="!form.template_id">
              <b-form-input v-model="form.subject" />
            </b-form-group>
            <b-form-group label="Message (HTML allowed)" v-if="!form.template_id">
              <b-form-textarea v-model="form.body_html" rows="8" />
            </b-form-group>
            <b-button type="submit" variant="primary" :disabled="isSending">
              {{ isSending ? 'Sending...' : 'Send Email' }}
            </b-button>
          </b-form>
        </b-tab>

        <!-- Bulk Email Tab -->
        <b-tab title="Bulk Email">
          <b-form @submit.prevent="handleBulkSend">
            <b-form-group label="From Email">
              <b-form-input v-model="bulkForm.from_email" placeholder="sender@school.com" required />
            </b-form-group>
            <b-form-group label="Recipient Mode">
              <b-form-radio-group
                v-model="bulkMode"
                :options="bulkModeOptions"
                buttons
                button-variant="outline-primary"
                size="sm"
              />
            </b-form-group>
            <b-form-group v-if="bulkMode === 'contacts'" label="Select Contacts">
              <v-select
                v-model="bulkForm.contact_ids"
                :options="contacts"
                label="text"
                :reduce="c => c.value"
                multiple
                placeholder="Search and select contacts..."
              />
            </b-form-group>
            <b-form-group v-if="bulkMode === 'tags'" label="Select Tags">
              <v-select
                v-model="bulkForm.tag_ids"
                :options="tags"
                label="name"
                :reduce="t => t.id"
                multiple
                placeholder="Search and select tags..."
              />
            </b-form-group>
            <b-form-group label="Use Template (optional)">
              <b-form-select v-model="bulkForm.template_id" :options="templateOptions" @change="onTemplateChange(bulkForm, 'bulk')" />
            </b-form-group>
            <b-form-group label="Subject" v-if="!bulkForm.template_id">
              <b-form-input v-model="bulkForm.subject" />
            </b-form-group>
            <b-form-group label="Message (HTML allowed)" v-if="!bulkForm.template_id">
              <b-form-textarea v-model="bulkForm.body_html" rows="8" />
            </b-form-group>
            <b-button type="submit" variant="primary" :disabled="isBulkSending">
              {{ isBulkSending ? 'Sending...' : 'Send Bulk Email' }}
            </b-button>
          </b-form>
        </b-tab>
      </b-tabs>
    </b-card>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import PageHeader from '../../components/Ui/PageHeader.vue';

export default {
  name: 'SendEmail',
  components: { PageHeader },
  data() {
    return {
      form: { from_email: '', contact_id: null, template_id: null, subject: '', body_html: '' },
      isSending: false,
      bulkMode: 'contacts',
      bulkModeOptions: [
        { text: 'Select Contacts', value: 'contacts' },
        { text: 'Select by Tag', value: 'tags' },
      ],
      bulkForm: {
        from_email: '',
        contact_ids: [],
        tag_ids: [],
        template_id: null,
        subject: '',
        body_html: '',
      },
      isBulkSending: false,
      contacts: [],
      templates: [],
      tags: [],
    };
  },
  computed: {
    templateOptions() {
      return [{ value: null, text: 'None — write below' }, ...this.templates.map(t => ({ value: t.id, text: t.name }))];
    },
  },
  methods: {
    ...mapActions('campaign', ['sendSingleEmail', 'sendBulkEmail']),
    ...mapActions('contact', ['getAllContacts']),
    ...mapActions('emailTemplate', ['getAllEmailTemplates']),
    ...mapActions('tag', ['getAllTags']),
    onTemplateChange(formObj /*, scope */) {
      // When a template is picked, clear inline subject/body — server uses template content.
      if (formObj.template_id) {
        formObj.subject = '';
        formObj.body_html = '';
      }
    },
    async handleSend() {
      if (!this.form.from_email) { this.makeToast('danger', 'From email is required'); return; }
      if (!this.form.contact_id) { this.makeToast('danger', 'Select a contact'); return; }
      if (!this.form.template_id && (!this.form.subject || !this.form.body_html)) {
        this.makeToast('danger', 'Pick a template, or provide subject and message');
        return;
      }
      this.isSending = true;
      try {
        await this.sendSingleEmail({
          contact_id: this.form.contact_id,
          template_id: this.form.template_id,
          from_email: this.form.from_email,
          subject: this.form.subject,
          body_html: this.form.body_html,
        });
        this.makeToast('success', 'Email send started');
        this.form = {
          from_email: this.form.from_email, // keep the from
          contact_id: null, template_id: null, subject: '', body_html: '',
        };
      } catch (err) {
        this.makeToast('danger', 'Send failed');
      } finally {
        this.isSending = false;
      }
    },
    async handleBulkSend() {
      if (!this.bulkForm.from_email) { this.makeToast('danger', 'From email is required'); return; }
      const hasContacts = this.bulkMode === 'contacts' && this.bulkForm.contact_ids.length > 0;
      const hasTags = this.bulkMode === 'tags' && this.bulkForm.tag_ids.length > 0;
      if (!hasContacts && !hasTags) {
        this.makeToast('danger', this.bulkMode === 'contacts' ? 'Select at least one contact' : 'Select at least one tag');
        return;
      }
      if (!this.bulkForm.template_id && (!this.bulkForm.subject || !this.bulkForm.body_html)) {
        this.makeToast('danger', 'Pick a template, or provide subject and message');
        return;
      }
      this.isBulkSending = true;
      try {
        await this.sendBulkEmail({
          contact_ids: this.bulkMode === 'contacts' ? this.bulkForm.contact_ids : [],
          tag_ids: this.bulkMode === 'tags' ? this.bulkForm.tag_ids : [],
          template_id: this.bulkForm.template_id,
          from_email: this.bulkForm.from_email,
          subject: this.bulkForm.subject,
          body_html: this.bulkForm.body_html,
        });
        this.makeToast('success', 'Bulk email send started');
        this.bulkForm = {
          from_email: this.bulkForm.from_email,
          contact_ids: [], tag_ids: [], template_id: null, subject: '', body_html: '',
        };
      } catch (err) {
        this.makeToast('danger', 'Bulk send failed');
      } finally {
        this.isBulkSending = false;
      }
    },
  },
  async created() {
    const user = this.$store.getters['auth/getLoggedInUser'];
    const defaultFrom = user && user.school ? (user.school.resend_from_email || '') : '';
    this.form.from_email = defaultFrom;
    this.bulkForm.from_email = defaultFrom;
    try {
      const [cRes, tRes, tagRes] = await Promise.all([
        this.getAllContacts({ limit: 1000 }),
        this.getAllEmailTemplates(),
        this.getAllTags(),
      ]);
      const contactData = cRes.data.results || cRes.data || [];
      this.contacts = contactData.map(c => ({
        value: c.id,
        text: `${c.first_name} ${c.last_name} (${c.email || 'no email'})`,
      }));
      this.templates = tRes.data.results || tRes.data || [];
      this.tags = tagRes.data.results || tagRes.data || [];
    } catch (err) { /* ignore */ }
  },
};
</script>
